import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import type { CtaRef } from './guided_fill.js';
import type { FlightPlannerCopy } from './planner_copy.js';

/*
 * Os botões de SAÍDA do planner de voo fora do rodapé de card: o "Buscar"
 * inline do naked/chat sem saia, o par Redefinir/Buscar do multi e o rótulo
 * default do CTA. Saíram de `planner_form.tsx` pelo teto de linhas dele — são
 * peças de apresentação puras, sem estado do form.
 */

/** O "Buscar" do NAKED (#2167) e do card do chat: o `Button` do DS em pílula,
 *  lupa + rótulo curto, a mesma peça nas duas casas (a fileira binária e a linha
 *  de status do multi) — o `VOLL/Button` de 36px do frame (#2362). Na linha do
 *  status a régua é a `default` (32px): ele divide a linha com uma frase de
 *  12px, e na altura da fileira dominaria a frase que conclui.
 *
 *  Com `iconOnly` (o card do chat, #2207) sobra só a lupa: o rótulo sai da tela e
 *  vira `aria-label`. É o mesmo gesto num espaço menor — dentro da bolha do chat a
 *  fileira não tem folga pra uma pílula com texto.
 *
 *  `genui2` no próprio botão: o card não vive sob a raiz de tema do DS (mesma
 *  razão do `FieldShell`); o `bg-primary` do átomo vence o fundo que o preflight
 *  escopado pinta no elemento do escopo. */
export function InlineSearchCta({
  ref,
  disabled,
  onClick,
  label,
  iconOnly,
  foot,
  className,
}: {
  /** O guia da página foca o Buscar quando o pedido fecha (`useGuidedFill`). */
  readonly ref: CtaRef;
  readonly disabled: boolean;
  readonly onClick: () => void;
  readonly label: string;
  readonly iconOnly?: boolean;
  /** Na linha do status do multi (`.fpl-legs-foot`): régua `default`, não `lg`. */
  readonly foot?: boolean;
  /** Só posição/largura (a réguas do estreito); o DS desenha. */
  readonly className?: string;
}) {
  const size = foot ? (iconOnly ? 'icon' : 'default') : iconOnly ? 'icon-lg' : 'lg';
  return (
    <Button
      ref={ref}
      type="button"
      size={size}
      className={`genui2 rounded-full${className ? ` ${className}` : ''}`}
      disabled={disabled}
      onClick={onClick}
      // O nome acessível some junto com o texto; sem isto o botão fica anônimo.
      {...(iconOnly ? { 'aria-label': label } : {})}
    >
      <MagnifyingGlassIcon aria-hidden />
      {!iconOnly && label}
    </Button>
  );
}

/** O Redefinir da saia do multi (#2362) — `Button` ghost do DS à esquerda do
 *  Buscar, a mesma receita do Cancelar da edição. Só nas réguas que são SAIA do
 *  componente (rodapé do card, linha do status do naked/inlineSearch); a faixa
 *  da bancada tem fecho próprio e não o recebe. `large` segue a régua do Buscar
 *  que ele acompanha (36 no rodapé, 32 na linha do status). */
export function MultiReset({
  onReset,
  copy,
  large,
  className,
}: {
  readonly onReset: () => void;
  readonly copy: FlightPlannerCopy;
  readonly large: boolean;
  /** Só posição/altura do estreito; o DS desenha. */
  readonly className?: string;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size={large ? 'lg' : 'default'}
      className={`genui2 bg-transparent${className ? ` ${className}` : ''}`}
      onClick={onReset}
    >
      {copy.multiLegReset}
    </Button>
  );
}

/** A saída do multi — a MESMA nas duas réguas: `default` (32px, cápsula) na linha
 *  do status da FAIXA, `lg` (36px) no rodapé do card (UX Review Pacote 2, M2).
 *  Sem `genui2` no card não haveria token pro `bg-primary` resolver. */
export function MultiCta({
  ref,
  large,
  disabled,
  onClick,
  ctaLabel,
}: {
  /** O Buscar que o guia foca com a viagem completa — só no rodapé do card. */
  readonly ref?: CtaRef;
  readonly large: boolean;
  /** `!legsValid(legs)` — o gate visual da viagem, decidido pelo form. */
  readonly disabled: boolean;
  readonly onClick: () => void;
  readonly ctaLabel: string;
}) {
  return (
    <Button
      ref={ref}
      type="button"
      size={large ? 'lg' : 'default'}
      className="genui2 rounded-full"
      disabled={disabled}
      onClick={onClick}
    >
      {ctaLabel}
    </Button>
  );
}

/** O rótulo DEFAULT do CTA de busca: curto no NAKED (a pílula inline não cabe o
 *  longo), o de sempre no card/faixa. A MESMA conta de naked-efetivo do
 *  `inlineCtas` — sem ela, `layout="band" naked` encurtava o CTA da faixa
 *  apesar da doc prometer que band/collapsed ignoram a prop (CodeRabbit #2181).
 *  Fora de `plannerLabels` pelo teto de complexidade dela. */
export function searchCtaLabel(
  naked: boolean | undefined,
  band: boolean,
  collapsed: boolean,
  copy: FlightPlannerCopy,
) {
  const nakedOpen = Boolean(naked) && !band && !collapsed;
  return nakedOpen ? copy.searchShort : copy.search;
}
